// Atlas · Unit economics — what one screen earns in a month, and who keeps it.
//
// A PEER to Deployment. Founder, 2026-09-19: "the unit economics of earnings
// from a device per month in plausible scenarios."
//
// NO PAYBACK ON THIS PAGE. An earlier cut divided a ₹25,000 device cost by
// every pocket. Founder, same day: "lets forget the recovery of the device,
// we are unnecessarily pegging it right now ... too much unnecessary things
// on the screen." Both halves of that are right. Recovery was the wrong
// question — ownership_history books every screen to CabE, so pDOOH had no
// hardware to recover and the figure was never about anything — and asking it
// dragged a payer toggle, a break-even bar and a column of months onto a page
// whose subject is a month's earnings.
//
// WHAT IS LEFT IS FOUR THINGS: the sentence, the windows, the split, and the
// arithmetic. Everything that qualifies those rather than states them is
// folded — still answering at one significant figure in its summary, because
// folded means unelaborated and never invisible.
//
// THE LADDER IS ORDERED BY PROVENANCE DATE, NEVER BY HEIGHT. An honest
// scenario set on this fleet is not a climb — September is below August, Pune
// taken whole is below the fleet — and reordering it into an ascent is the one
// thing this page exists to refuse.

import React, { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import Term from '../Term';
import useApi from '../../v2/lib/useApi';
import api from '../../services/api';

const n = (v) => (v == null ? '—' : Number(v).toLocaleString('en-IN'));
const inr = (v, dp = 0) => (v == null ? '—'
  : `₹${Number(v).toLocaleString('en-IN', { minimumFractionDigits: dp, maximumFractionDigits: dp })}`);
const pct = (v, dp = 1) => (v == null ? '—' : `${(Number(v) * 100).toFixed(dp)}%`);

export default function Unit() {
  const { data, error, loading, reload } = useApi(
    () => api.get('/unit/economics').then((r) => r.data), []);
  const [params, setParams] = useSearchParams();

  const setParam = (k, v) => {
    const next = new URLSearchParams(params);
    if (!v) next.delete(k); else next.set(k, v);
    setParams(next, { replace: true });
  };

  const scope = useMemo(() => {
    if (!data) return null;
    return data.scopes.find((s) => s.id === params.get('s'))
      || data.scopes.find((s) => s.id === 'sep') || data.scopes[0];
  }, [data, params]);

  if (loading) return <div className="atlasState">Reading production…</div>;
  if (error) {
    return (
      <div className="atlasState atlasStateErr">
        <p>Could not build the unit economics.</p>
        <p className="atlasStateDetail">{String(error)}</p>
        <button type="button" className="atlasBtn" onClick={reload}>Try again</button>
      </div>
    );
  }
  if (!data || !scope) return null;

  const s = scope;
  const e = s.economics;
  const dist = data.distribution;

  return (
    <div className="ue">
      {/* ── A · which window ──────────────────────────────────────────── */}
      <div className="ueBar">
        <span className="ueBarLabel">window</span>
        <div className="ueSource" role="group" aria-label="Which measured window">
          {data.scopes.map((sc) => (
            <button
              key={sc.id}
              type="button"
              className={`ueChip${sc.id === s.id ? ' isOn' : ''}`}
              onClick={() => setParam('s', sc.id === 'sep' ? null : sc.id)}
            >
              {sc.label}
              {/* n inside the chip, never in a footnote — one cab and sixteen
                  cabs must not look alike. */}
              <i>{sc.n.cabs} cab{sc.n.cabs > 1 ? 's' : ''} · {n(sc.n.fittedCabDays)} fitted days</i>
            </button>
          ))}
        </div>
        <span className="ueStamp">{data.ist}</span>
      </div>

      {/* ── the top band: what it earns, the figures, and what is sold ── */}
      <div className="ueTop">
        <header className="ueLede">
          <p className="ueKicker">
            {data.fleet.operator} · Mumbai and Pune · {s.label}
            {s.n.partial && <> · {s.n.elapsedDays} of {s.n.daysInMonth} days at a {s.n.daysInMonth}-day rate</>}
          </p>
          <h1 className="ueSentence">
            One fitted screen earns <b>{inr(s.gross)}</b> of{' '}
            <Term k="monthRevenue">presence-verified gross</Term> a month.{' '}
            <Term k="technology">Technology</Term> takes {data.rates.technologyPct}% off the top, and of what
            remains only <b>{pct(s.gate.shareOfRevenue)}</b> is{' '}
            <Term k="metGateMonth">shareable with supply</Term> — the rest was earned on days no passenger
            stayed long enough to meet the gate, and pDOOH.ai keeps it.{' '}
            {e.premise != null && (
              <>So the split is <b>{inr(e.pdoohNet)}</b> to pDOOH.ai, <b>{inr(e.premise)}</b> to the
                premise owner and <b>{inr(e.device)}</b> to the device owner — one pocket of{' '}
                <b>{inr(e.supplyNet)}</b> today, because CabE is both.</>
            )}
          </h1>
        </header>

        <dl className="ueFigures">
          <div>
            <dt><Term k="monthRevenue">Gross</Term></dt>
            <dd>{inr(s.gross)}</dd>
            <dd className="ueSub">per screen-month</dd>
          </div>
          <div className="isLead">
            <dt>pDOOH.ai</dt>
            <dd>{inr(e.pdoohNet)}</dd>
            <dd className="ueSub">after SIM, gateway, bad debt</dd>
          </div>
          <div>
            <dt>CabE, net</dt>
            <dd>{inr(e.supplyNet)}</dd>
            <dd className="ueSub">both pockets, less SIM</dd>
          </div>
          <div>
            <dt><Term k="metGateMonth">Shareable</Term></dt>
            <dd>{pct(s.gate.shareOfRevenue)}</dd>
            <dd className="ueSub">{pct(s.gate.passRateOfDays)} of cab-days</dd>
          </div>
          <div>
            <dt><Term k="screensFitted">Screens</Term></dt>
            <dd>{s.n.cabs}</dd>
            <dd className="ueSub">{s.n.deliveringCabs} delivering</dd>
          </div>
          <div>
            <dt>Best of them</dt>
            <dd>{inr(dist.best)}</dd>
            <dd className="ueSub">{dist.best && dist.mean ? `${(dist.best / dist.mean).toFixed(1)}× the mean` : ''}</dd>
          </div>
        </dl>

        <div className="ueCard isWarn ueBookCard">
          <p className="ueCardHead">
            <b>The book</b>
            <span>{data.demand.externalCount} live · all end {data.demand.bookEnds}</span>
          </p>
          {data.demand.external.map((b) => (
            <p key={`${b.tenant}-${b.campaign}`}><b>{b.tenant}</b> <i>{b.campaign}</i></p>
          ))}
          <p className="ueCardNote">{data.legend.fleetMultiplier}</p>
        </div>
      </div>

      <div className="ueMain">
      <section className="ueLadder">
        <div className="ueSectHead">
          <h2>Every window we can actually measure</h2>
          <p>
            Ordered by date, never by height — the honest set is not a climb. Each row draws all five of
            its factors from one coherent window, because a row assembled from two sources is a row that
            never happened.
          </p>
        </div>
        <div className="ueLadderScroll">
          <table className="ueTable">
            <thead>
              <tr>
                <th scope="col">window</th>
                <th scope="col">gross</th>
                <th scope="col"><Term k="metGateMonth">shareable</Term></th>
                <th scope="col">pDOOH.ai</th>
                <th scope="col">premise</th>
                <th scope="col">device</th>
                <th scope="col">CabE, net</th>
              </tr>
            </thead>
            <tbody>
              {data.scopes.map((sc) => {
                const ec = sc.economics;
                return (
                  <tr key={sc.id} className={sc.id === s.id ? 'isOn' : undefined}>
                    <th scope="row">
                      <button type="button" onClick={() => setParam('s', sc.id === 'sep' ? null : sc.id)}>
                        {sc.label}
                      </button>
                      <i>{sc.n.cabs} cab{sc.n.cabs > 1 ? 's' : ''} · {n(sc.n.fittedCabDays)} fitted cab-days
                        {sc.n.partial && <> · scaled ×{sc.n.scaleFactor}</>}</i>
                    </th>
                    <td>{inr(sc.gross)}</td>
                    <td>{pct(sc.gate.shareOfRevenue)}</td>
                    <td className="ueLeadCell">{inr(ec.pdoohNet)}</td>
                    <td>{inr(ec.premise)}</td>
                    <td>{inr(ec.device)}</td>
                    <td>{inr(ec.supplyNet)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>

      <section className="ueFall">
        <div className="ueSectHead">
          <h2>Where it goes</h2>
          <p>{s.label}, per fitted screen-month.</p>
        </div>
        <dl className="ueRows">
          <Row label="Gross" k="monthRevenue" amount={inr(e.gross)} cls="isTop" />
          <Row label={`Technology ${data.rates.technologyPct}%`} k="technology"
            amount={`−${inr(e.tech)}`} scope="off the top, of gross" />
          <Row label="Pool" k="pool" amount={inr(e.pool)} cls="isSum" />
          <Row label="Met the gate" k="metGateEcon" amount={inr(e.qualified)} cls="isHead"
            scope={`${pct(s.gate.shareOfRevenue)} of REVENUE — not the ${pct(s.gate.passRateOfDays)} of days`} />
          <Row label="Did not meet it" k="didNotMeet" amount={inr(e.unqualified)} cls="isChild"
            scope="pDOOH.ai keeps it" />
          <Row label="Not scored yet" k="notScored" amount={inr(e.unscored)} cls="isChild"
            scope="no verdict either way — the sweep scores T−1 at 09:00 IST, so this is today's money, not missed money" />
          <Row label="On a screen with a registered owner" k="shareablePool" amount={inr(e.shareable)} cls="isSum"
            scope={`${pct(e.ownedFrac)} of the gate-passing pool — read for this window, never carried from another`} />
          <Row label="No owner on record" k="noOwner" amount={inr(e.house)} cls="isCarve"
            scope="booked house to pDOOH.ai" />
          <Row label={`Premise owner ${data.rates.premisePct}%`} k="premiseOwner" amount={inr(e.premise)} cls="isChild" />
          <Row label={`Device owner ${data.rates.devicePct}%`} k="deviceOwner" amount={inr(e.device)} cls="isChild" />
          <Row label={`pDOOH.ai margin ${data.rates.marginPct}%`} k="platformMargin" amount={inr(e.margin)} cls="isChild" />
          <Row label="Supply gross" k="supplyShare" amount={inr(e.supplyGross)} cls="isTotal" />
          <Row label="SIM recovery" k="simRecovery" amount={`−${inr(e.simRecovery)}`} cls="isCarve"
            scope={`${inr(data.rates.simPerDeviceMonth)} × ${s.n.deliveringCabs} that delivered ÷ ${s.n.meanFitted} mean-fitted`} />
          <Row label="Supply, net" k="toSupplyNet" amount={inr(e.supplyNet)} cls="isTotal"
            scope="not clamped at zero — when little meets the gate this goes negative and the operator ends the month owing us" />
          <Row label="pDOOH.ai take" k="pdoohTake" amount={inr(e.pdoohTake)} cls="isTotal" />
          <Row label="SIM cost to the telco" amount={`−${inr(e.costs.simCost)}`} cls="isCarve isAssumed"
            scope="assumed, mirroring the billed rate — the SIM crosses twice or not at all, or we book a credit for connectivity nobody paid for" />
          <Row label={`Gateway ${e.costs.mdrRate.toFixed(2)}%`} amount={`−${inr(e.costs.mdr)}`} cls="isCarve isAssumed"
            scope="prospective — Easebuzz went live 14 Sep; not one rupee here came through it" />
          <Row label={`Bad debt ${data.rates.badDebtPct}%`} amount={`−${inr(e.costs.badDebt)}`} cls="isCarve"
            scope="₹416 uncollected of ₹1,16,469 raised — measured, and it is two events" />
          <Row label="pDOOH.ai net" k="pdoohNet" amount={inr(e.pdoohNet)} cls="isTotal isNet" />
        </dl>
      </section>
      </div>

      {/* EVERYTHING THAT QUALIFIES THE ABOVE, ACROSS AND NOT DOWN. These were
          five stacked <details>, which on a desktop is a queue rather than a
          layout — the reader had to open each one to find out whether it was
          worth opening. Four columns, all open, all legible. */}
      <div className="ueCards">
        <div className="ueCard">
          <p className="ueCardHead">
            <b>The fleet behind the average</b>
            <span>{dist.darkCount} of {dist.screens.length} earned nothing</span>
          </p>
          <div className="ueStrip">
            {dist.screens.map((d) => (
              <span
                key={d.plate}
                className={`ueMark${d.dark ? ' isDark' : ''}`}
                style={{ left: `${Math.min(100, (d.grossPerMonth / (dist.best || 1)) * 100)}%` }}
                title={`${d.plate} · ${d.city} · ${inr(d.grossPerMonth)} per 30-day month`}
              />
            ))}
            <span className="ueMean" style={{ left: `${((dist.mean || 0) / (dist.best || 1)) * 100}%` }}>
              <i>mean {inr(dist.mean)}</i>
            </span>
          </div>
          <p>The mean is not a screen you could point at. Dark: <i>{dist.screens.filter((d) => d.dark).map((d) => d.plate).join(', ')}</i></p>
        </div>

        <div className="ueCard">
          <p className="ueCardHead">
            <b>Where the gross comes from</b>
            <span>a decomposition, not a validation</span>
          </p>
          <ul className="ueLinks">
            <li>attendance <b>{pct(s.factors.attendance)}</b></li>
            <li>plays per working day <b>{n(Math.round(s.factors.playsPerWorkingDay))}</b></li>
            <li>occupancy <b>{pct(s.factors.occupancy)}</b></li>
            <li className="isWelded">price per occupied play <b>{s.factors.paisePerOccupied.toFixed(2)}p</b></li>
            <li>× {s.factors.days} days <b>{inr(s.gross)}</b></li>
          </ul>
          <p>
            The five are defined so they must multiply back — every numerator cancels the next
            denominator, so a zero residual was never available to fail. They attribute a change;
            they do not confirm one. Price is <i>welded</i>: a constant in impressionPricing.js,
            not a price that cleared.
          </p>
        </div>

        <div className="ueCard">
          <p className="ueCardHead">
            <b>Windows we refuse to show</b>
            <span>{data.killed.length} refuted</span>
          </p>
          {data.killed.map((k) => (
            <p key={k.id}>
              <b className="ueStruck">{k.label}</b>
              {k.refutedGross ? <i className="ueStruck">{inr(k.refutedGross)} — </i> : ''}
              <i>{k.why.split('. ').slice(0, 2).join('. ')}.</i>
            </p>
          ))}
        </div>

        <div className="ueCard">
          <p className="ueCardHead">
            <b>Assumed, not measured</b>
            <span>◇ marks them above</span>
          </p>
          {Object.entries(data.assumptions.provenance).map(([k, v]) => (
            <p key={k}><b>{k}</b> <i>{v}</i></p>
          ))}
          <p className="ueCardNote">{data.ownership.note}</p>
          <p className="ueCardNote">
            Nothing here records install labour, device refresh, power, support, insurance, or the
            serving stack. Those costs are real and are not on this page.
          </p>
        </div>
      </div>
    </div>
  );
}

// One row of the waterfall. The scope clause rides with its label rather than
// living in a footnote, because a figure that does not say what it is a figure
// OF answers wrongly — and on this page the most common misreading is a right
// number taken for a claim about something else.
function Row({ label, k, amount, cls = '', scope }) {
  return (
    <div className={`ueRow ${cls}`}>
      <dt>
        {k ? <Term k={k}>{label}</Term> : label}
        {scope && <em className="ueRowScope">{scope}</em>}
      </dt>
      <dd>{amount}</dd>
    </div>
  );
}
